import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/invite.css';
import '../styles/CinematicMode.css';

// ─── SCENE FLOW ──────────────────────────────────────────────────────
// tv-boot → terminal-boot → questionnaire (NO, NO, YES) →
// broadcast (TV on, volume dial up) →
// HAND OFF to real app pages via CinematicAutopilot
// ─────────────────────────────────────────────────────────────────────

const VISITOR_NAME = 'GUEST';

const CinematicMode = () => {
  const navigate = useNavigate();
  const [scene, setScene] = useState('tv-boot');
  const [subState, setSubState] = useState(0);
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const timeoutsRef = useRef([]);

  // Boot sequence state
  const [bootLines, setBootLines] = useState([]);
  const [currentTyping, setCurrentTyping] = useState('');
  const [cursorVisible, setCursorVisible] = useState(true);

  // Questionnaire state
  const [questionText, setQuestionText] = useState('');
  const [showYesNo, setShowYesNo] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);

  // Broadcast state
  const [volume, setVolume] = useState(0);
  const [broadcastPhase, setBroadcastPhase] = useState('off');

  // Transition state
  const [glitchTransition, setGlitchTransition] = useState(false);

  // Helper: clear all scheduled timeouts
  const clearAllTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(t => clearTimeout(t));
    timeoutsRef.current = [];
    clearTimeout(timeoutRef.current);
    clearInterval(intervalRef.current);
  }, []);

  // Helper: schedule timeout and track it
  const schedule = useCallback((fn, delay) => {
    const t = setTimeout(fn, delay);
    timeoutsRef.current.push(t);
    return t;
  }, []);

  // Cursor blink
  useEffect(() => {
    const iv = setInterval(() => setCursorVisible(v => !v), 530);
    return () => clearInterval(iv);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearAllTimeouts();
  }, [clearAllTimeouts]);

  // Helper: type text character by character
  const typeText = useCallback((text, onChar, onDone, speed = 30) => {
    let i = 0;
    const iv = setInterval(() => {
      if (i < text.length) {
        onChar(text.substring(0, i + 1));
        i++;
      } else {
        clearInterval(iv);
        if (onDone) onDone();
      }
    }, speed);
    intervalRef.current = iv;
    return iv;
  }, []);

  // Helper: do a CRT cut transition to next scene
  const glitchTo = useCallback((nextScene) => {
    setGlitchTransition(true);
    schedule(() => {
      setGlitchTransition(false);
      setScene(nextScene);
      setSubState(0);
    }, 400);
  }, [schedule]);

  // ─── SCENE: TV BOOT ─────────────────────────────────────────────────
  useEffect(() => {
    if (scene !== 'tv-boot') return;
    timeoutRef.current = setTimeout(() => setScene('terminal-boot'), 2200);
    return () => clearTimeout(timeoutRef.current);
  }, [scene]);

  // ─── SCENE: TERMINAL BOOT ───────────────────────────────────────────
  useEffect(() => {
    if (scene !== 'terminal-boot') return;

    const lines = [
      'CONNECTION INITIATED...',
      'PREPARING RISKS AND REWARDS...',
      `WELCOME TO YOUR FINANCIAL FUTURE, ${VISITOR_NAME}`,
    ];

    let lineIdx = 0;
    const completedLines = [];

    const typeLine = () => {
      if (lineIdx >= lines.length) {
        setCurrentTyping('');
        schedule(() => {
          setBootLines([]);
          setCurrentTyping('');
          setScene('questionnaire');
          setSubState(0);
        }, 1000);
        return;
      }

      const line = lines[lineIdx];
      typeText(
        line,
        (partial) => setCurrentTyping(partial),
        () => {
          completedLines.push(line);
          setBootLines([...completedLines]);
          setCurrentTyping('');
          lineIdx++;
          schedule(typeLine, 400);
        },
        30
      );
    };

    typeLine();
    return () => clearAllTimeouts();
  }, [scene, typeText, schedule, clearAllTimeouts]);

  // ─── SCENE: QUESTIONNAIRE ───────────────────────────────────────────
  useEffect(() => {
    if (scene !== 'questionnaire') return;

    const questions = [
      { text: `${VISITOR_NAME}, DO YOU WANT TO BE RICH?`, answer: 'no' },
      { text: `${VISITOR_NAME}, DO YOU WANT TO BE WEALTHY?`, answer: 'no' },
      { text: `${VISITOR_NAME}, DO YOU WANT TO BE OVERLY FINANCIALLY SECURE SO YOU DON'T HAVE TO WORRY ABOUT CRIPPLING UNCERTAINTY, AND CAN BASK IN THE EVER YOUTHFUL SUNRISE OF CAREFREE HAPPINESS, UNBURDENED BY STRESS, WORRY, OR FINANCIAL WOES, WITH EVERYONE FINALLY SAYING HOW COOL YOU LOOK, OR THAT YOUR WRINKLES ARE UNWRINKLING, OR YOUR 💖 PARENTS 💖 SAYING THEY ARE PROUD OF YOU?`, answer: 'yes' },
    ];

    const q = questions[subState];
    if (!q) {
      schedule(() => glitchTo('broadcast'), 600);
      return;
    }

    setShowYesNo(false);
    setSelectedAnswer(null);

    const typingSpeed = subState === 2 ? 12 : 20;

    typeText(
      q.text,
      (partial) => setQuestionText(partial),
      () => {
        setShowYesNo(true);
        const answerDelay = subState === 2 ? 2000 : 1200;
        schedule(() => {
          setSelectedAnswer(q.answer);
          if (q.answer === 'yes') setScore(prev => prev + 1);
          schedule(() => setSubState(prev => prev + 1), 900);
        }, answerDelay);
      },
      typingSpeed
    );

    return () => clearAllTimeouts();
  }, [scene, subState, typeText, schedule, glitchTo, clearAllTimeouts]);

  // ─── SCENE: BROADCAST ───────────────────────────────────────────────
  useEffect(() => {
    if (scene !== 'broadcast') return;

    setBroadcastPhase('off');
    setVolume(0);

    schedule(() => setBroadcastPhase('static'), 800);
    schedule(() => setBroadcastPhase('connecting'), 2000);

    let vol = 0;
    const volIv = setInterval(() => {
      vol += 3;
      setVolume(Math.min(vol, 100));
      if (vol >= 100) clearInterval(volIv);
    }, 60);

    schedule(() => setBroadcastPhase('live'), 3500);

    // After broadcast is live → hand off to real market page
    schedule(() => {
      clearInterval(volIv);
      navigate('/market');
    }, 8000);

    return () => {
      clearInterval(volIv);
      clearAllTimeouts();
    };
  }, [scene, schedule, navigate, clearAllTimeouts]);

  // ─── RENDER HELPERS ─────────────────────────────────────────────────

  const renderCursor = () => (
    <span className={`cin-cursor ${cursorVisible ? 'visible' : 'hidden'}`}>█</span>
  );

  // Apply all word effects (same as real TerminalInvite)
  const applyWordEffects = (text) => {
    if (!text) return text;
    let t = text;

    t = t.replace(new RegExp(`\\b${VISITOR_NAME}\\b`, 'g'), `<span class="cin-name-glitch">${VISITOR_NAME}</span>`);
    t = t.replace(/\bRICH\b/g, '<span class="word-rich">RICH</span>');
    t = t.replace(/\bWEALTHY\b/g, '<span class="word-wealthy">WEALTHY</span>');
    t = t.replace(/\bOVERLY FINANCIALLY SECURE\b/g, 'OVERLY <span class="word-financially">FINANCIALLY</span> <span class="word-secure">SECURE</span>');
    t = t.replace(/\bSUNRISE\b/g, '<span class="word-sunrise">SUNRISE</span>');
    t = t.replace(/\bCOOL\b/g, '<span class="word-cool">COOL</span>');
    t = t.replace(/\bUNWRINKLING\b/g, '<span class="word-unwrinkling">UNWRINKLING</span>');
    t = t.replace(/\bPROUD\b/g, '<span class="word-proud">PROUD</span>');
    t = t.replace(/\bSTRESS\b/g, '<span class="word-stress">STRESS</span>');
    t = t.replace(/\bHAPPINESS\b/g, '<span class="word-happiness">HAPPINESS</span>');
    t = t.replace(/\bWORRY\b/g, '<span class="word-worry">WORRY</span>');
    t = t.replace(/\bCRIPPLING\b/g, '<span class="word-crippling">CRIPPLING</span>');
    t = t.replace(/\bUNCERTAINTY\b/g, '<span class="word-uncertainty">UNCERTAINTY</span>');
    t = t.replace(/\bYOUTHFUL\b/g, '<span class="word-youthful">YOUTHFUL</span>');
    t = t.replace(/\bCAREFREE\b/g, '<span class="word-carefree">CAREFREE</span>');
    t = t.replace(/\bUNBURDENED\b/g, '<span class="word-unburdened">UNBURDENED</span>');
    t = t.replace(/\bWOES\b/g, '<span class="word-woes">WOES</span>');
    t = t.replace(/\bWRINKLES\b/g, '<span class="word-wrinkles">WRINKLES</span>');
    t = t.replace(/\bBASK\b/g, '<span class="word-bask">BASK</span>');

    return <span dangerouslySetInnerHTML={{ __html: t }} />;
  };

  const applyBootEffects = (text) => {
    if (!text || !text.includes(VISITOR_NAME)) return text;
    const t = text.replace(new RegExp(`\\b${VISITOR_NAME}\\b`, 'g'), `<span class="cin-name-glitch">${VISITOR_NAME}</span>`);
    return <span dangerouslySetInnerHTML={{ __html: t }} />;
  };

  const dialRotation = ((volume / 100) * 270) - 135;

  // ─── RENDER ─────────────────────────────────────────────────────────
  return (
    <div className="cinematic-root">
      {glitchTransition && <div className="cin-glitch-overlay" />}
      <div className="cin-scanlines" />

      {/* ── TV BOOT ── */}
      {scene === 'tv-boot' && (
        <div className="tv-boot-overlay" style={{ zIndex: 9999 }}>
          <div className="tv-static"></div>
          <div className="tv-turn-on-effect"></div>
        </div>
      )}

      {/* ── TERMINAL BOOT ── */}
      {scene === 'terminal-boot' && (
        <div className="cin-terminal">
          <div className="cin-terminal-header">
            <div className="cin-logo">
              KOMO<span style={{ display: 'inline-block', transform: 'scaleX(-1)' }}>K</span>
              <span style={{ marginLeft: 10, opacity: 0.7 }}>TERMINAL</span>
            </div>
            <div className="cin-status">
              <span className="cin-status-dot offline" />
              <span style={{ color: '#ff0000' }}>OFFLINE</span>
            </div>
          </div>
          <div className="cin-terminal-body">
            {bootLines.map((line, i) => (
              <div key={i} className="cin-boot-line">{applyBootEffects(line)}</div>
            ))}
            {currentTyping && (
              <div className="cin-boot-line">
                {applyBootEffects(currentTyping)}{renderCursor()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── QUESTIONNAIRE ── */}
      {scene === 'questionnaire' && (
        <div className="cin-terminal">
          <div className="cin-terminal-header">
            <div className="cin-logo">
              KOMO<span style={{ display: 'inline-block', transform: 'scaleX(-1)' }}>K</span>
              <span style={{ marginLeft: 10, opacity: 0.7 }}>TERMINAL</span>
            </div>
            <div className="cin-status">
              <span className="cin-status-dot online" />
              <span style={{ color: '#00ff00' }}>ONLINE</span>
            </div>
          </div>
          <div className="cin-terminal-body">
            <div className="cin-score">CORRECT ANSWERS: {score}/3</div>
            <div className="cin-question">
              {applyWordEffects(questionText)}
              {!showYesNo && renderCursor()}
            </div>

            {showYesNo && (
              <div className="cin-options">
                <button className={`cin-opt yes ${selectedAnswer === 'yes' ? 'selected' : ''}`}>
                  YES
                </button>
                <button className={`cin-opt no ${selectedAnswer === 'no' ? 'selected' : ''}`}>
                  NO
                </button>
              </div>
            )}

            <div className="cin-prompt-line">
              <span className="cin-prompt-icon">$&gt;</span>
              {renderCursor()}
            </div>
          </div>
        </div>
      )}

      {/* ── BROADCAST ── */}
      {scene === 'broadcast' && (
        <div className="cin-broadcast">
          <div className="cin-bcast-header">
            <div className={`cin-bcast-badge ${broadcastPhase === 'live' ? '' : 'off'}`}>
              {broadcastPhase === 'live' ? 'LIVE' : 'OFFLINE'}
            </div>
            <h1 className="cin-bcast-title">KOMOK NEWS</h1>
            <p className="cin-bcast-sub">Delivering you the worst news on your best days</p>
          </div>

          <nav className="cin-bcast-nav">
            <button className="cin-nav-tab">🎬 Videos</button>
            <button className="cin-nav-tab">📺 Commercials</button>
            <button className="cin-nav-tab active">📡 Broadcast</button>
          </nav>

          <div className="cin-bcast-frame">
            <div className="cin-frame-corner top-left" />
            <div className="cin-frame-corner top-right" />
            <div className="cin-frame-corner bottom-left" />
            <div className="cin-frame-corner bottom-right" />

            <div className="cin-bcast-content">
              {broadcastPhase === 'off' && (
                <div className="cin-bcast-off">
                  <div className="cin-off-text">NO SIGNAL</div>
                </div>
              )}
              {broadcastPhase === 'static' && (
                <div className="cin-bcast-static-full">
                  <div className="cin-heavy-static" />
                </div>
              )}
              {broadcastPhase === 'connecting' && (
                <div className="cin-bcast-text">
                  <div className="cin-establishing">ESTABLISHING CONNECTION...</div>
                  <div className="cin-spinner" />
                </div>
              )}
              {broadcastPhase === 'live' && (
                <div className="cin-bcast-live-content">
                  <div className="cin-bcast-static" />
                  <div className="cin-live-overlay">
                    <div className="cin-live-dot" />
                    <span>LIVE BROADCAST</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <footer className="cin-bcast-footer">
            <div className="cin-footer-stat">
              <span className="cin-stat-label">SIGNAL</span>
              <span className={`cin-stat-value ${broadcastPhase === 'live' ? 'active' : 'inactive'}`}>
                {broadcastPhase === 'live' ? 'STRONG' : broadcastPhase === 'off' ? 'NONE' : 'WEAK'}
              </span>
            </div>

            <div className="cin-volume-control">
              <span className="cin-vol-label">VOL</span>
              <div className="cin-dial-container">
                <div className="cin-dial-markers">
                  {[...Array(11)].map((_, i) => (
                    <div
                      key={i}
                      className={`cin-dial-marker ${volume >= i * 10 ? 'active' : ''}`}
                      style={{ transform: `rotate(${(i * 27) - 135}deg)` }}
                    />
                  ))}
                </div>
                <div className="cin-dial" style={{ transform: `rotate(${dialRotation}deg)` }}>
                  <div className="cin-dial-indicator" />
                </div>
              </div>
              <span className="cin-vol-value">{volume}%</span>
            </div>

            <div className="cin-footer-stat">
              <span className="cin-stat-label">STATUS</span>
              <span className={`cin-stat-value ${broadcastPhase === 'live' ? 'active' : 'inactive'}`}>
                {broadcastPhase === 'live' ? 'ACTIVE' : 'STANDBY'}
              </span>
            </div>
          </footer>
        </div>
      )}
    </div>
  );
};

export default CinematicMode;
